import * as React from 'react'
import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { ListItem, Dropdown } from '../../../../common/src/components/Dropdown'
import { Spacer, IntroTextSpan, BodyText, InputMed, TextArea } from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import { addFeedback } from '../../../../common/src/feedback'
import Time from '../../../../common/src/time'
import { JobName } from '../../../../common/src/types.gen'
import { runAdminTaskUncaught } from '../../graphql/mutations'

export interface AdminJobSummary {
  text: string
  argumentDescription?: string
  argumentRequired?: boolean
  description?: string
  args: { name: JobName; firmId?: number }
}

type AdminJobSummaryListItem = AdminJobSummary & ListItem<number>

/**
 * Given a list of `AdminJobSummary` items, allows you to select and invoke any of them, along
 * with an optional argument. Provides descriptions of what jobs do and what their arguments are.
 */
export function JobsRunningSection({ jobs: adminJobs }: { jobs: AdminJobSummary[] }) {
  return (
    <div>
      <Spacer height={16} />
      <AdminJobSection descs={adminJobs} />
    </div>
  )
}

function SuccessOrErrorRunningJob({ state }: { state: 'idle' | 'running' | 'success' | 'error' }) {
  if (state === 'success' || state === 'error') {
    const stateIsSuccessOrError = state === 'success' ? 'mint' : 'coral'
    const stateText = state === 'success' ? 'submitted job' : state === 'error' ? 'error submitting job' : ''

    return <IntroTextSpan color={stateIsSuccessOrError}>{stateText}</IntroTextSpan>
  }

  return null
}

function AdminJobSection({ descs }: { descs: AdminJobSummary[] }) {
  const listItems = descs.map((d, i) => Object.assign(d, { id: i, name: d.text }))
  const [selectedItem, setSelectedItem] = useState<AdminJobSummaryListItem | undefined>(undefined)
  const [state, setState] = useState<'idle' | 'running' | 'success' | 'error'>('idle')
  const [result, setResult] = useState<string | undefined>(undefined)
  const [value, setValue] = useState('')

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value)
  }, [])

  const onItemChange = React.useCallback(
    (item: AdminJobSummaryListItem | undefined) => {
      if (value) {
        setValue('')
      }
      setState('idle')
      setResult(undefined)
      setSelectedItem(item)
    },
    [value]
  )

  const brieflyChangeStateTo = React.useCallback((stateStr: 'idle' | 'running' | 'success' | 'error') => {
    setState(stateStr)
    setTimeout(() => setState('idle'), 2000)
  }, [])

  const copyResult = React.useCallback(() => {
    if (result) {
      copyStringToClipboard(result)
      addFeedback('Copied result')
    }
  }, [result])

  const runJob = React.useCallback(() => {
    const args = selectedItem?.args

    const input = value === '' ? null : value
    const fullArgs = Object.assign(args as any, { input })

    if (value === '' && selectedItem?.argumentRequired) {
      setState('error')
      setResult('please input an argument.')
      return
    }

    setState('running')
    setResult(undefined)

    runAdminTaskUncaught(fullArgs as any)
      .then(result => {
        setResult(result?.data?.runAdminTask)
        brieflyChangeStateTo('success')
        if (selectedItem?.text === 'Set Time') {
          Time.syncNow()
        }
      })
      .catch(err => {
        console.log(err)
        brieflyChangeStateTo('error')
      })
  }, [value, selectedItem])

  return (
    <div>
      <AdminJobDropdownSection
        listItems={listItems}
        selectedItem={selectedItem}
        onItemChange={onItemChange}
        runJob={runJob}
        state={state}
      />
      <Spacer width={16} />
      <SuccessOrErrorRunningJob state={state} />
      {selectedItem != null && <AdminJobDescriptionSection selectedItem={selectedItem} />}
      <Spacer height={8} />
      {selectedItem?.text === 'Set Time' && <TimestampGeneratorSection />}
      <AdminJobInputSection selectedItem={selectedItem} value={value} onChange={handleChange} />
      <Spacer height={8} />
      {result && (
        <>
          <Button size="small" onClick={copyResult}>
            Copy result
          </Button>
          <Spacer height={8} />
          <Result>{result}</Result>
        </>
      )}
    </div>
  )
}

interface AdminJobInputSectionProps {
  selectedItem?: AdminJobSummaryListItem
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}

function AdminJobInputSection(props: AdminJobInputSectionProps) {
  const { selectedItem, value, onChange } = props
  return (
    <AdminInput
      style={{
        display: !selectedItem || !selectedItem.argumentDescription ? 'none' : 'initial',
      }}
      placeholder={selectedItem && selectedItem.argumentDescription}
      value={value}
      onChange={onChange}
    />
  )
}

interface AdminJobDropdownSectionProps {
  listItems: AdminJobSummaryListItem[]
  selectedItem?: AdminJobSummaryListItem
  onItemChange: (item?: AdminJobSummaryListItem) => void
  runJob: () => void
  state: 'idle' | 'running' | 'success' | 'error'
}

function AdminJobDropdownSection(props: AdminJobDropdownSectionProps) {
  const { listItems, selectedItem, onItemChange, runJob, state } = props
  return (
    <>
      <Dropdown
        items={listItems}
        selectedItemId={selectedItem?.id}
        onSelectedItemChange={onItemChange}
        placeholder={'select an admin job to run'}
        disabled={state === 'running'}
        width={500}
      />
      <Spacer width={16} />
      <Button size="small" disabled={state === 'running' || !selectedItem} onClick={runJob}>
        Run Job
      </Button>
    </>
  )
}

interface AdminJobDescriptionSectionProps {
  selectedItem: AdminJobSummaryListItem
}

function AdminJobDescriptionSection(props: AdminJobDescriptionSectionProps) {
  const { selectedItem } = props
  return (
    <DescriptionContainer>
      <ReactMarkdown className="markdown-body lisa-markdown-dark">{selectedItem.description || ''}</ReactMarkdown>
    </DescriptionContainer>
  )
}

function TimestampGeneratorSection() {
  return (
    <>
      <Spacer height={8} />
      <TimestampGenerator />
      <Spacer height={8} />
    </>
  )
}

interface TimestampGeneratorState {
  now: Date
}

/**
 * Generates a UTC timestamp for submitting `Set Time` admin job.
 * Input datetime is based on the client's local time.
 */
class TimestampGenerator extends React.PureComponent<Record<string, never>, TimestampGeneratorState> {
  private timer?: NodeJS.Timeout
  private dateInputRef: React.RefObject<HTMLInputElement>
  private timeInputRef: React.RefObject<HTMLInputElement>

  constructor(props: Record<string, never>) {
    super(props)
    this.state = {
      now: Time.now(),
    }
    this.dateInputRef = React.createRef<HTMLInputElement>()
    this.timeInputRef = React.createRef<HTMLInputElement>()
  }

  componentDidMount() {
    this.timer = setInterval(this.updateDate, 1000)
  }

  componentWillUnmount() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }

  private updateDate = () => {
    this.setState({ now: Time.now() })
  }

  private copyTimestamp = () => {
    const newDate = this.validateInputDate()

    if (newDate == null) {
      return
    }

    copyStringToClipboard(newDate.toISOString())
    addFeedback('Copied timestamp')
  }

  private validateInputDate = () => {
    const datetimeString = this.dateInputRef.current?.value + 'T' + this.timeInputRef.current?.value
    const newDate = new Date(datetimeString)

    if (newDate.toString() === 'Invalid Date') {
      addFeedback('Invalid Date')
      return null
    }

    return newDate
  }

  render() {
    const { now } = this.state

    return (
      <div>
        <BodyText>{now.toString()}</BodyText>
        <Spacer height={8} />
        <DatetimeInput ref={this.dateInputRef} type="date" />
        <Spacer width={8} />
        <DatetimeInput ref={this.timeInputRef} type="time" />
        <Spacer width={8} />
        <Button size="small" onClick={this.copyTimestamp}>
          Copy timestamp
        </Button>
      </div>
    )
  }
}

const DatetimeInput = styled(InputMed)`
  /* make icon color white */
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`

function copyStringToClipboard(str: string) {
  const textArea = document.createElement('textarea')
  textArea.value = str
  textArea.setAttribute('readonly', '')
  textArea.setAttribute('type', 'hidden')
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand('copy')
  document.body.removeChild(textArea)
}
/* ************* styled components only below ************* */

const AdminInput = styled(TextArea)`
  width: 100%;
  font-family: monospace;
`

const DescriptionContainer = styled.div`
  width: 100%;
`

const Result = styled.pre`
  font-family: monospace;
  width: 100%;
  background-color: ${Colors.steel};
  color: ${Colors.white};
  padding: 16px;
  border-radius: 4px;
  box-sizing: border-box;
  word-wrap: break-word;
  white-space: pre-wrap;
`
